const SCROLL_SELECTORS = '[data-scroll-container], .tiptap-editor, .cm-scroller'

export function findDocumentScrollElement(root: ParentNode | null | undefined): HTMLElement | null {
  if (!root) return null
  // Prefer the root itself when it is the scroller (shared preview/edit container).
  // querySelector only matches descendants - without this, capture/restore against the
  // data-scroll-container element falls through to a nested .tiptap-editor with scrollTop 0.
  if (root instanceof HTMLElement && root.matches(SCROLL_SELECTORS)) {
    return root
  }
  return root.querySelector<HTMLElement>(SCROLL_SELECTORS)
}

/** Ratio (0-1) for an explicit scroll element (e.g. CodeMirror `.cm-scroller`). */
export function captureScrollRatioFromElement(scrollEl: HTMLElement | null | undefined): number | null {
  if (!scrollEl) return null
  const scrollable = scrollEl.scrollHeight - scrollEl.clientHeight
  if (scrollable <= 0) return 0
  return scrollEl.scrollTop / scrollable
}

/** Capture scroll position as a ratio (0-1) so it survives content height changes. */
export function captureDocumentScrollRatio(root: ParentNode | null | undefined): number | null {
  return captureScrollRatioFromElement(findDocumentScrollElement(root))
}

/** Restore scroll on a known element; retries while layout settles. */
export function restoreScrollRatioOnElement(
  scrollEl: HTMLElement | null | undefined,
  ratio: number,
  maxAttempts = 30
): void {
  const clamped = Math.max(0, Math.min(1, ratio))
  let attempts = 0

  const tryRestore = () => {
    if (scrollEl) {
      const scrollable = scrollEl.scrollHeight - scrollEl.clientHeight
      if (scrollable > 0) {
        scrollEl.scrollTop = clamped * scrollable
        return
      }
    }
    attempts += 1
    if (attempts < maxAttempts) {
      requestAnimationFrame(tryRestore)
    }
  }

  requestAnimationFrame(tryRestore)
}

/** Restore scroll after editor remount; retries until the scroll container exists. */
export function restoreDocumentScrollRatio(
  root: ParentNode | null | undefined,
  ratio: number,
  maxAttempts = 30
): void {
  const clamped = Math.max(0, Math.min(1, ratio))
  let attempts = 0

  const tryRestore = () => {
    const scrollEl = findDocumentScrollElement(root)
    if (scrollEl) {
      const scrollable = scrollEl.scrollHeight - scrollEl.clientHeight
      if (scrollable > 0) {
        scrollEl.scrollTop = clamped * scrollable
        return
      }
    }
    attempts += 1
    if (attempts < maxAttempts) {
      requestAnimationFrame(tryRestore)
    }
  }

  requestAnimationFrame(tryRestore)
}
